import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import LocalTypeahead from '../components/ui/LocalTypeahead';
import ColumnSearchBar from '../components/segmentBuilder/ColumnSearchBar';
import FolderPathSelect, { NO_FOLDER_SENTINEL } from '../components/segmentBuilder/FolderPathSelect';
import '../components/segmentBuilder/CriteriaBuilder.css';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function normalizeFolderList(folders) {
  if (!Array.isArray(folders)) return [];
  return folders.map((p) => String(p || '').trim()).filter(Boolean);
}

function isNoFolderSelection(folders) {
  return Array.isArray(folders) && folders.length === 1 && folders[0] === NO_FOLDER_SENTINEL;
}

function formatListCell(value, separator = ', ') {
  if (value == null) return '—';
  if (Array.isArray(value)) {
    return value.length ? value.join(separator) : '—';
  }
  const s = String(value).trim();
  return s || '—';
}

function formatVisible(value) {
  if (value === true || value === 'true' || value === 'True') return 'True';
  if (value === false || value === 'false' || value === 'False') return 'False';
  if (value == null || value === '') return '—';
  return String(value);
}

function rowKey(row, index) {
  return `${row.schema || ''}-${row.table_name || ''}-${row.column_name || ''}-${index}`;
}

function parseCommaList(raw) {
  return String(raw || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
}

function parseSlashList(raw) {
  return String(raw || '')
    .split('/')
    .map((p) => p.trim())
    .filter(Boolean);
}

const PENDING_APPLY_STORAGE_KEY = 'dataDictionaryNeedsApply';

function readPendingApplyFlag() {
  try {
    return localStorage.getItem(PENDING_APPLY_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writePendingApplyFlag(value) {
  try {
    if (value) {
      localStorage.setItem(PENDING_APPLY_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(PENDING_APPLY_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode errors
  }
}

function EditableCell({
  display,
  editKind,
  initialDraft,
  disabled,
  saving,
  onSave,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialDraft);

  const startEdit = () => {
    if (disabled || saving) return;
    setDraft(initialDraft);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (saving) return;
    setEditing(false);
    setDraft(initialDraft);
  };

  const commit = async () => {
    if (saving) return;
    const ok = await onSave(draft);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 min-w-0">
        {editKind === 'visible' ? (
          <select
            className="h-8 rounded-md border border-input bg-background px-2 text-sm min-w-[5.5rem]"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            aria-label="Edit visible"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        ) : (
          <Input
            className="h-8 py-1 min-w-[8rem] flex-1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            disabled={saving}
            autoFocus
            aria-label="Edit value"
          />
        )}
        <button
          type="button"
          className="p-1 rounded hover:bg-blue-50 text-[#0062FB] disabled:opacity-50 shrink-0"
          onClick={commit}
          disabled={saving}
          aria-label="Save"
          title="Save"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        <button
          type="button"
          className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-50 shrink-0"
          onClick={cancelEdit}
          disabled={saving}
          aria-label="Cancel"
          title="Cancel"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1 min-w-0 group">
      <span className="min-w-0 break-words leading-5">{display}</span>
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-blue-50 text-gray-400 hover:text-[#0062FB] disabled:opacity-40 shrink-0"
        onClick={startEdit}
        disabled={disabled || saving}
        aria-label="Edit"
        title="Edit"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

function DataDictionaryResultsTable({ hits, onRowUpdated, onFieldEdited }) {
  const [savingCell, setSavingCell] = useState(null);
  const [cellError, setCellError] = useState('');

  const saveField = async (row, index, field, payload) => {
    const cellId = `${rowKey(row, index)}:${field}`;
    setSavingCell(cellId);
    setCellError('');
    try {
      const res = await api.patch('/api/v2/data-dictionary/row', {
        schema: row.schema ?? '',
        table_name: row.table_name ?? '',
        column_name: row.column_name ?? '',
        ...payload,
      });
      const updated = res.data?.hit;
      if (updated && onRowUpdated) {
        onRowUpdated(index, updated);
      }
      if (onFieldEdited) onFieldEdited();
      return true;
    } catch (e) {
      setCellError(formatApiErrorDetail(e.response?.data?.detail) || 'Update failed');
      return false;
    } finally {
      setSavingCell(null);
    }
  };

  return (
    <div className="space-y-2">
      {cellError && (
        <p className="text-sm text-red-600" role="alert">
          {cellError}
        </p>
      )}
      <div className="border border-gray-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Schema</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Table</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Column name</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Display name</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">Visible</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Description</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Tags</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Folder</th>
            </tr>
          </thead>
          <tbody>
            {hits.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-gray-500 text-center">
                  No columns found
                </td>
              </tr>
            ) : (
              hits.map((row, index) => {
                const key = rowKey(row, index);
                const busy = savingCell != null;
                const visibleDraft =
                  row.visible === false || row.visible === 'false' || row.visible === 'False'
                    ? 'false'
                    : 'true';
                return (
                  <tr key={key} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 align-top text-gray-800 whitespace-nowrap leading-5">
                      {row.schema || '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800 whitespace-nowrap leading-5">
                      {row.table_name || '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800 font-bold whitespace-nowrap leading-5">
                      {row.column_name || '—'}
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800 whitespace-nowrap">
                      <EditableCell
                        display={row.display_name || '—'}
                        editKind="text"
                        initialDraft={row.display_name || ''}
                        disabled={busy && savingCell !== `${key}:display_name`}
                        saving={savingCell === `${key}:display_name`}
                        onSave={async (draft) =>
                          saveField(row, index, 'display_name', { display_name: draft })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800 whitespace-nowrap">
                      <EditableCell
                        display={formatVisible(row.visible)}
                        editKind="visible"
                        initialDraft={visibleDraft}
                        disabled={busy && savingCell !== `${key}:visible`}
                        saving={savingCell === `${key}:visible`}
                        onSave={async (draft) =>
                          saveField(row, index, 'visible', { visible: draft === 'true' })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800 max-w-md">
                      <EditableCell
                        display={row.description || '—'}
                        editKind="text"
                        initialDraft={row.description || ''}
                        disabled={busy && savingCell !== `${key}:description`}
                        saving={savingCell === `${key}:description`}
                        onSave={async (draft) =>
                          saveField(row, index, 'description', { description: draft })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800">
                      <EditableCell
                        display={formatListCell(row.tags)}
                        editKind="text"
                        initialDraft={Array.isArray(row.tags) ? row.tags.join(', ') : String(row.tags || '')}
                        disabled={busy && savingCell !== `${key}:tags`}
                        saving={savingCell === `${key}:tags`}
                        onSave={async (draft) =>
                          saveField(row, index, 'tags', { tags: parseCommaList(draft) })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 align-top text-gray-800">
                      <EditableCell
                        display={formatListCell(row.folder_list, ' / ')}
                        editKind="text"
                        initialDraft={
                          Array.isArray(row.folder_list)
                            ? row.folder_list.join(' / ')
                            : String(row.folder_list || '')
                        }
                        disabled={busy && savingCell !== `${key}:folder_list`}
                        saving={savingCell === `${key}:folder_list`}
                        onSave={async (draft) =>
                          saveField(row, index, 'folder_list', {
                            folder_list: parseSlashList(draft.replace(/\s*\/\s*/g, '/')),
                          })
                        }
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DataDictionary = () => {
  const { user } = useAuth();
  const [hasFastCubeAccess, setHasFastCubeAccess] = useState(null);
  const [columnLabel, setColumnLabel] = useState('');
  const [columnFilter, setColumnFilter] = useState('');
  const [folderPath, setFolderPath] = useState([]);
  const [visibleFilter, setVisibleFilter] = useState('any');
  const [completeFilter, setCompleteFilter] = useState('any');
  const [tagFilter, setTagFilter] = useState('');
  const [tagOptions, setTagOptions] = useState([]);
  const [hits, setHits] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [needsApply, setNeedsApply] = useState(() => readPendingApplyFlag());
  const [error, setError] = useState('');
  const [applyError, setApplyError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (!cancelled) {
          setHasFastCubeAccess(organizationsService.hasFastCubeAccess(orgs));
        }
      } catch {
        if (!cancelled) setHasFastCubeAccess(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (hasFastCubeAccess !== true) return undefined;
    let cancelled = false;
    const loadTags = async () => {
      try {
        const res = await api.get('/api/v2/segments/tags');
        if (!cancelled) {
          setTagOptions(Array.isArray(res.data) ? res.data.filter(Boolean) : []);
        }
      } catch {
        if (!cancelled) setTagOptions([]);
      }
    };
    loadTags();
    return () => {
      cancelled = true;
    };
  }, [hasFastCubeAccess]);

  const markNeedsApply = () => {
    writePendingApplyFlag(true);
    setNeedsApply(true);
  };

  const handleColumnSelect = (col) => {
    const label = col.display_name || col.column_name || col.column || '';
    setColumnLabel(label);
    setColumnFilter(col.column || col.column_name || '');
    const folders = normalizeFolderList(col.folder_list);
    if (folders.length) {
      setFolderPath(folders);
    } else {
      setFolderPath([NO_FOLDER_SENTINEL]);
    }
  };

  const handleColumnClear = () => {
    setColumnLabel('');
    setColumnFilter('');
    setFolderPath([]);
  };

  const handleFolderPathChange = (nextPath) => {
    if (isNoFolderSelection(nextPath)) {
      setFolderPath([NO_FOLDER_SENTINEL]);
    } else {
      setFolderPath(normalizeFolderList(nextPath));
    }
    setColumnLabel('');
    setColumnFilter('');
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setApplyError('');
    setSearched(true);
    try {
      const params = {};
      if (columnFilter.trim()) {
        params.column_name = columnFilter.trim();
      }
      if (visibleFilter === 'true') {
        params.visible = true;
      } else if (visibleFilter === 'false') {
        params.visible = false;
      }
      if (completeFilter === 'yes') {
        params.complete = true;
      } else if (completeFilter === 'no') {
        params.complete = false;
      }
      if (tagFilter.trim()) {
        params.tag = tagFilter.trim();
      }
      if (isNoFolderSelection(folderPath)) {
        params.folder_null = true;
      } else {
        const folders = normalizeFolderList(folderPath);
        if (folders.length) {
          params.folder_path = folders.join(',');
        }
      }
      const res = await api.get('/api/v2/data-dictionary/search', { params });
      setHits(Array.isArray(res.data?.hits) ? res.data.hits : []);
    } catch (e) {
      setHits([]);
      setError(formatApiErrorDetail(e.response?.data?.detail) || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!needsApply || applying) return;
    setApplying(true);
    setApplyError('');
    try {
      await api.post('/api/v2/data-dictionary/apply');
      writePendingApplyFlag(false);
      setNeedsApply(false);
    } catch (e) {
      setApplyError(formatApiErrorDetail(e.response?.data?.detail) || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleRowUpdated = (index, updated) => {
    setHits((prev) => prev.map((row, i) => (i === index ? { ...row, ...updated } : row)));
  };

  if (!user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (hasFastCubeAccess === null) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  if (!hasFastCubeAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const columnSearchParams = {};
  if (visibleFilter === 'true') {
    columnSearchParams.visible = true;
  } else if (visibleFilter === 'false') {
    columnSearchParams.visible = false;
  } else {
    // Explicit null = any (including not visible), so ColumnSearchBar skips its default true.
    columnSearchParams.visible = null;
  }
  if (isNoFolderSelection(folderPath)) {
    columnSearchParams.folder_null = true;
  } else {
    const folders = normalizeFolderList(folderPath);
    if (folders.length) {
      columnSearchParams.folder_path = folders.join(',');
    }
  }

  const folderVisibleFilter =
    visibleFilter === 'true' ? true : visibleFilter === 'false' ? false : null;

  return (
    <Card className="overflow-visible">
      <CardHeader>
        <CardTitle>Data Dictionary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 overflow-visible">
        <p className="text-sm text-gray-600">
          Manage column metadata for the segment builder catalog. Non-visible folders and columns
          are included so they can be found and edited.
        </p>

        <div className="criteria-builder space-y-3">
          <div>
            <Label className="mb-1.5 block">Column</Label>
            <ColumnSearchBar
              selectedLabel={columnLabel}
              onSelect={handleColumnSelect}
              onClear={handleColumnClear}
              searchParams={columnSearchParams}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">Folder path</Label>
            <FolderPathSelect
              value={folderPath}
              onChange={handleFolderPathChange}
              includeNoFolderOption
              visible={folderVisibleFilter}
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="max-w-xs w-full sm:w-48">
              <Label className="mb-1.5 block">Visible</Label>
              <FunctionalSelect
                value={visibleFilter}
                onValueChange={setVisibleFilter}
                placeholder="Any"
              >
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </FunctionalSelect>
            </div>

            <div className="max-w-xs w-full sm:w-48">
              <Label className="mb-1.5 block">Complete</Label>
              <FunctionalSelect
                value={completeFilter}
                onValueChange={setCompleteFilter}
                placeholder="Any"
              >
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </FunctionalSelect>
            </div>

            <div className="max-w-xs w-full sm:w-48">
              <Label className="mb-1.5 block">Tag</Label>
              <LocalTypeahead
                options={tagOptions}
                value={tagFilter}
                onChange={setTagFilter}
                placeholder="Any"
                ariaLabel="Filter by tag"
                emptyMessage="No tags found"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" type="button" onClick={handleSearch} disabled={loading || applying}>
              {loading ? 'Searching…' : 'Search'}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={handleApply}
              disabled={!needsApply || applying || loading}
            >
              {applying ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {applyError && (
          <p className="text-sm text-red-600" role="alert">
            {applyError}
          </p>
        )}

        {searched && !loading && !error && (
          <DataDictionaryResultsTable
            hits={hits}
            onRowUpdated={handleRowUpdated}
            onFieldEdited={markNeedsApply}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DataDictionary;
